import React, { useState, useEffect } from 'react';
import Editor from '@draft-js-plugins/editor';
import {
    EditorState,
    ContentState,
    convertFromHTML,
    Modifier,
    SelectionState,
    getDefaultKeyBinding,
    RichUtils,
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import '@draft-js-plugins/static-toolbar/lib/plugin.css';
import PropTypes from 'prop-types';
import { IconButton, Typography } from '@mui/material';

const editorContainerStyle = {
    border: '1px solid #ddd',
    minHeight: 200,
    padding: 10,
};

const toolbarStyle = {
    display: 'flex',
    gap: '8px',
};

const boldIconStyle = {
    fontWeight: 'bold',
    fontSize: '1.25rem',
};

const italicIconStyle = {
    fontStyle: 'italic',
    fontWeight: 'bold',
    fontSize: '1.25rem',
};

const iconButtonBaseSx = {
    width: 'auto',
    padding: '6px 16px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease',
};

export default function RichTextEditor(props) {
    const dataDecoded = Buffer.from(props.initialData, 'base64').toString('utf-8');
    const blocksFromHTML = convertFromHTML(dataDecoded);
    const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);

    // Set up the editor state
    const [editorState, setEditorState] = useState(() => EditorState.createWithContent(contentState));

    useEffect(() => {
        if (typeof props.getEditorContentHtml === 'function') {
            props.getEditorContentHtml(() => {
                const currentContent = editorState.getCurrentContent();
                const htmlContent = stateToHTML(currentContent);
                return Buffer.from(htmlContent, 'utf-8').toString('base64');
            });
        }
    }, [editorState, props]);

    const customKeyBindingFn = (e) => {
        if (e.key === 'Enter') {
            return 'split-block';
        }
        return getDefaultKeyBinding(e);
    };

    const handleKeyCommand = (command, editorState, setEditorState) => {
        if (command === 'split-block') {
            const selection = editorState.getSelection();
            const contentState = editorState.getCurrentContent();
            const blockType = contentState.getBlockForKey(selection.getStartKey()).getType();

            if (blockType === 'header-one' || blockType === 'header-two') {
                // Split the block
                const newContentState = Modifier.splitBlock(contentState, selection);

                // Get the key of the new block
                const newBlockKey = newContentState.getKeyAfter(selection.getStartKey());

                // Change the new block to 'unstyled'
                const updatedContentState = Modifier.setBlockType(
                    newContentState,
                    SelectionState.createEmpty(newBlockKey),
                    'unstyled'
                );

                // Push the updated content state
                let newEditorState = EditorState.push(editorState, updatedContentState, 'split-block');

                // Move the selection to the new block
                const newSelection = SelectionState.createEmpty(newBlockKey);
                newEditorState = EditorState.forceSelection(newEditorState, newSelection);

                // Set editorstate
                setEditorState(newEditorState);

                return 'handled';
            }
        }
        return 'not-handled';
    };

    function CustomToolbar({ editorState, setEditorState }) {
        const currentBlockType = editorState
            .getCurrentContent()
            .getBlockForKey(editorState.getSelection().getStartKey())
            .getType();

        const currentInlineStyle = editorState.getCurrentInlineStyle();

        const toggleBlockType = (blockType) => {
            const newEditorState = RichUtils.toggleBlockType(editorState, blockType);
            setEditorState(newEditorState);
        };

        const toggleInlineStyle = (style) => {
            const newEditorState = RichUtils.toggleInlineStyle(editorState, style);
            setEditorState(newEditorState);
        };

        const isActive = (style) => currentInlineStyle.has(style);

        return (
            <div style={{ marginBottom: '10px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>
                <div style={toolbarStyle}>
                    <IconButton
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleBlockType('header-one');
                        }}
                        sx={{
                            ...iconButtonBaseSx,
                            bgcolor: currentBlockType === 'header-one' ? 'primary.main' : 'transparent',
                            '&:hover': { bgcolor: currentBlockType === 'header-one' ? 'primary.light' : undefined },
                        }}>
                        <Typography variant="h6">H1</Typography>
                    </IconButton>

                    <IconButton
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleBlockType('header-two');
                        }}
                        sx={{
                            ...iconButtonBaseSx,
                            bgcolor: currentBlockType === 'header-two' ? 'primary.main' : 'transparent',
                            '&:hover': { bgcolor: currentBlockType === 'header-two' ? 'primary.light' : undefined },
                        }}>
                        <Typography variant="h6">H2</Typography>
                    </IconButton>

                    <IconButton
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleBlockType('unstyled');
                        }}
                        sx={{
                            ...iconButtonBaseSx,
                            bgcolor: currentBlockType === 'unstyled' ? 'primary.main' : 'transparent',
                            '&:hover': { bgcolor: currentBlockType === 'unstyled' ? 'primary.light' : undefined },
                        }}>
                        <Typography variant="body1">teksti</Typography>
                    </IconButton>

                    <IconButton
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleInlineStyle('BOLD');
                        }}
                        sx={{
                            ...iconButtonBaseSx,
                            bgcolor: isActive('BOLD') ? 'primary.main' : 'transparent',
                            '&:hover': { bgcolor: isActive('BOLD') ? 'primary.light' : undefined },
                        }}>
                        <Typography variant="body1" style={boldIconStyle}>
                            B
                        </Typography>
                    </IconButton>

                    <IconButton
                        onMouseDown={(e) => {
                            e.preventDefault();
                            toggleInlineStyle('ITALIC');
                        }}
                        sx={{
                            ...iconButtonBaseSx,
                            bgcolor: isActive('ITALIC') ? 'primary.main' : 'transparent',
                            '&:hover': { bgcolor: isActive('ITALIC') ? 'primary.light' : undefined },
                        }}>
                        <Typography variant="body1" style={italicIconStyle}>
                            I
                        </Typography>
                    </IconButton>
                </div>
            </div>
        );
    }

    return (
        <div style={editorContainerStyle}>
            {!props.viewOnly && <CustomToolbar editorState={editorState} setEditorState={setEditorState} />}
            <Editor
                editorState={editorState}
                onChange={setEditorState}
                readOnly={props.viewOnly}
                keyBindingFn={customKeyBindingFn}
                handleKeyCommand={(command) => handleKeyCommand(command, editorState, setEditorState)}
            />
        </div>
    );
}

RichTextEditor.propTypes = {
    htmlContent: PropTypes.string,
    viewOnly: PropTypes.bool.isRequired,
    getEditorContentHtml: PropTypes.func,
};
